import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const input = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-saffron";

export default function ChangePassword({ testid = "change-password-btn", triggerClass = "", label = "Change Password", iconOnly = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async () => {
    if (!current || !next) { toast.error("Fill in all password fields"); return; }
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed — please log in again");
      setOpen(false);
      reset();
      await logout();
      navigate("/login");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testid} aria-label={label} className={triggerClass}>
        <KeyRound className={iconOnly ? "h-4 w-4" : "h-3.5 w-3.5"} />{!iconOnly && ` ${label}`}
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid={`${testid}-modal`}>
          <div className="absolute inset-0 bg-black/60" onClick={() => { setOpen(false); reset(); }} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-navy">Change Password</h2>
              <button onClick={() => { setOpen(false); reset(); }} data-testid={`${testid}-close`} aria-label="Close"
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-500">Current password</label>
                <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                       data-testid={`${testid}-current`} className={input} autoComplete="current-password" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-500">New password (min 8 characters)</label>
                <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                       data-testid={`${testid}-new`} className={input} autoComplete="new-password" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-slate-500">Confirm new password</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       data-testid={`${testid}-confirm`} className={input} autoComplete="new-password"
                       onKeyDown={(e) => e.key === "Enter" && submit()} />
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">You will be logged out and asked to log in with the new password.</p>
            <button onClick={submit} disabled={busy} data-testid={`${testid}-submit`}
                    className="mt-4 w-full rounded-lg bg-saffron py-2.5 text-sm font-bold text-white transition-colors hover:bg-saffron-hover disabled:opacity-50">
              {busy ? "Updating…" : "Update Password"}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
